import React, { useEffect, useState } from "react";
import {
  Box,
  Flex,
  Heading,
  Text,
  Container,
  VStack,
  Input,
  Select,
  Button,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Alert,
  AlertIcon,
  Link,
} from "@chakra-ui/react";
import { Link as RouterLink } from "react-router-dom";
import { getMe, updateMe } from "../api/auth";
import { useAuth } from "../auth/AuthContext";

function isValidEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

const PAYMENT_METHODS = [
  { value: "paypal", label: "PayPal", placeholder: "PayPal email or link" },
  { value: "venmo", label: "Venmo", placeholder: "@venmo-username" },
  { value: "zelle", label: "Zelle", placeholder: "Email or phone number" },
  { value: "cash_app", label: "Cash App", placeholder: "$cashtag" },
];

export default function ProfilePage() {
  const { updateUser } = useAuth();

  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [preferred, setPreferred] = useState("");
  const [paymentHandle, setPaymentHandle] = useState("");

  const [loadingProfile, setLoadingProfile] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  useEffect(() => {
    async function loadProfile() {
      try {
        setLoadingProfile(true);
        const me = await getMe();
        setEmail(me.email || "");
        setUsername(me.username || "");
        setPreferred(me.preferred_payment_method || "");
        setPaymentHandle(me.payment_handle || "");
      } catch (err) {
        const msg =
          err?.response?.data?.detail ||
          err?.message ||
          "Unable to load profile.";
        setApiError(typeof msg === "string" ? msg : "Unable to load profile.");
      } finally {
        setLoadingProfile(false);
      }
    }

    loadProfile();
  }, []);

  const emailTrim = email.trim();
  const usernameTrim = username.trim();

  const handlePlaceholder =
    PAYMENT_METHODS.find((m) => m.value === preferred)?.placeholder || "Handle";

  const emailError = !emailTrim
    ? "Email is required."
    : !isValidEmail(emailTrim)
      ? "Please enter a valid email address."
      : "";

  const usernameError = !usernameTrim
    ? "Username is required."
    : usernameTrim.length < 3
      ? "Username must be at least 3 characters."
      : "";

  async function onSubmit(e) {
    e.preventDefault();
    setApiError("");
    setSuccessMessage("");

    if (emailError || usernameError) return;

    try {
      setSubmitting(true);

      const updated = await updateMe({
        email: emailTrim,
        username: usernameTrim,
        preferred_payment_method: preferred || null,
        payment_handle: paymentHandle.trim(),
      });

      updateUser(updated);
      setSuccessMessage("Profile updated successfully.");
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        err?.message ||
        "Unable to update profile.";

      setApiError(typeof msg === "string" ? msg : "Unable to update profile.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Box minH="100vh" bg="#00007f" color="white">
      <Box bg="#00005f" borderBottom="2px solid #7a7a7a" boxShadow="md">
        <Container maxW="container.xl" py={5}>
          <Heading
            fontFamily="'Orbitron', sans-serif"
            fontSize="40px"
            letterSpacing="3px"
            fontWeight="600"
          >
            THE DEEP END
          </Heading>
        </Container>
      </Box>

      <Container maxW="container.md" py={12}>
        <VStack spacing={8} align="stretch">
          <VStack spacing={2} align="start">
            <Heading size="lg">Update Profile</Heading>
            <Text color="whiteAlpha.900">
              Update your username and email address.
            </Text>
          </VStack>

          <Box
            as="form"
            onSubmit={onSubmit}
            bg="white"
            color="black"
            border="3px solid #00005f"
            borderRadius="lg"
            boxShadow="lg"
            px={6}
            py={8}
          >
            <VStack spacing={4}>
              {apiError ? (
                <Alert status="error" borderRadius="md">
                  <AlertIcon />
                  {apiError}
                </Alert>
              ) : null}

              {successMessage ? (
                <Alert status="success" borderRadius="md">
                  <AlertIcon />
                  {successMessage}
                </Alert>
              ) : null}

              <FormControl isInvalid={!!emailError && email.length > 0} isDisabled={loadingProfile}>
                <FormLabel>Email</FormLabel>
                <Input
                  placeholder="Email"
                  type="email"
                  bg="white"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                />
                <FormErrorMessage>{emailError}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!usernameError && username.length > 0} isDisabled={loadingProfile}>
                <FormLabel>Username</FormLabel>
                <Input
                  placeholder="Username"
                  bg="white"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  autoComplete="username"
                />
                <FormErrorMessage>{usernameError}</FormErrorMessage>
              </FormControl>

              <FormControl isDisabled={loadingProfile}>
                <FormLabel>Preferred Payment Method</FormLabel>
                <Flex gap={3} direction={{ base: "column", sm: "row" }}>
                  <Select
                    bg="white"
                    placeholder="Select method"
                    value={preferred}
                    onChange={(e) => setPreferred(e.target.value)}
                    maxW={{ base: "100%", sm: "200px" }}
                  >
                    {PAYMENT_METHODS.map(({ value, label }) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </Select>
                  <Input
                    placeholder={handlePlaceholder}
                    bg="white"
                    value={paymentHandle}
                    onChange={(e) => setPaymentHandle(e.target.value)}
                  />
                </Flex>
                <Text fontSize="xs" color="gray.500" mt={1}>
                  Optional — how you&apos;d prefer to be paid out.
                </Text>
              </FormControl>

              <Button
                type="submit"
                bg="#00005f"
                color="white"
                w="100%"
                isLoading={submitting || loadingProfile}
                loadingText={loadingProfile ? "Loading" : "Saving"}
                _hover={{ bg: "#00004a" }}
                isDisabled={loadingProfile}
              >
                Save Profile
              </Button>

              <Text fontSize="sm">
                <Link as={RouterLink} to="/" color="#00005f" fontWeight="600">
                  Back to Home
                </Link>
              </Text>
            </VStack>
          </Box>
        </VStack>
      </Container>
    </Box>
  );
}